/**
 * 1.7.8 (D-1.7.8-3) — "This runs into 1104 at 3:30 by 20 minutes." and the three choices: Push (with every card that
 * would move and its new time) · Keep both as they are · Cancel. The line for anything that would end after the
 * day's end — in the danger colour, with an icon and in words; the push is still allowed. When the next visit has
 * started it cannot be pushed: Keep both and Cancel only.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { OverlapAnswer, PastEnd } from '@shared/day-bend';
import { clockWithPeriod, formatClock } from '@shared/dayplan';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/Icons';
import { useAnnounce } from '@/components/Announcer';

export interface OverlapChoiceProps {
  answer: OverlapAnswer;
  /** The apartment being booked or moved. */
  unit: string;
  busy?: boolean;
  onPush: () => void;
  /** 15 Oct 2026: trade times with the visit already there (offered on a move). */
  onSwap?: () => void;
  onKeep: () => void;
  onCancel: () => void;
}

export function OverlapChoice({ answer, unit, busy, onPush, onSwap, onKeep, onCancel }: OverlapChoiceProps) {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'es' = i18n.language.startsWith('es') ? 'es' : 'en';
  const announce = useAnnounce();
  const heading = useRef<HTMLHeadingElement | null>(null);
  const { overlap, push } = answer;
  const dayEnd = clockWithPeriod(answer.dayEnd, lang);
  const words = t('when178.overlap.words', { unit: overlap.unit, at: formatClock(overlap.at), count: overlap.by });
  // a visit the crew is inside cannot move; one that is simply already there (or a swap is offered) is not "started"
  const started = push || answer.swap || (overlap.occupant && !overlap.started) ? null : t('when178.overlap.started', { unit: overlap.unit });
  const said = started ? `${words} ${started}` : words;

  // the step replaced the form: the keyboard and the screen reader go to its sentence
  useEffect(() => {
    heading.current?.focus();
    announce(said, 'assertive');
  }, [said, announce]);

  const pastEnd = (rows: PastEnd[], key: 'when178.overlap.pastEndPush' | 'when178.overlap.pastEndKeep', testid: string) =>
    rows.length ? (
      <div className="overlap-past-end" data-testid={testid}>
        {rows.map((p) => (
          <p className="past-end-line" key={`${p.id ?? 'new'}-${p.unit}`}>
            <Icon name="alert" aria-hidden="true" />
            <span>{t(key, { unit: p.unit, end: clockWithPeriod(p.end, lang), dayEnd })}</span>
          </p>
        ))}
      </div>
    ) : null;

  return (
    <section className="overlap178" aria-labelledby="overlap-words-h" data-testid="overlap-step">
      <h3 id="overlap-words-h" className="overlap-words" tabIndex={-1} ref={heading} data-testid="overlap-words">
        {words}
      </h3>
      {answer.swap && onSwap ? (
        <div className="overlap-choice">
          <Button type="button" disabled={busy} onClick={onSwap} data-testid="overlap-swap">
            {busy ? t('when178.overlap.sending') : t('when178.overlap.swap', { unit: overlap.unit, to: formatClock(answer.swap.to) })}
          </Button>
          <p className="help">{t('when178.overlap.swapHelp', { unit, other: overlap.unit, to: formatClock(answer.swap.to) })}</p>
        </div>
      ) : null}
      {push ? (
        <div className="overlap-choice">
          <Button type="button" disabled={busy} onClick={onPush} aria-describedby="overlap-moves-list" data-testid="overlap-push">
            {busy ? t('when178.overlap.sending') : t('when178.overlap.push', { unit: overlap.unit, to: formatClock(push.moves[0]?.to ?? overlap.at + push.by) })}
          </Button>
          <ul className="overlap-moves" id="overlap-moves-list" aria-label={t('when178.overlap.movesLabel')} data-testid="overlap-moves">
            {push.moves.map((m) => (
              <li key={m.id}>{t('when178.overlap.move', { unit: m.unit, from: formatClock(m.from), to: formatClock(m.to) })}</li>
            ))}
          </ul>
          {pastEnd(answer.pastEndIfPushed, 'when178.overlap.pastEndPush', 'overlap-past-end')}
        </div>
      ) : !started ? null : (
        <div className="warn" role="note" data-testid="overlap-started">
          <Icon name="lock" aria-hidden="true" />
          <div>{started}</div>
        </div>
      )}
      <div className="overlap-choice">
        <Button variant="secondary" type="button" disabled={busy} onClick={onKeep} aria-describedby="overlap-keep-help" data-testid="overlap-keep">
          {t('when178.overlap.keep')}
        </Button>
        <p className="help" id="overlap-keep-help">
          {t('when178.overlap.keepHelp', { unit, other: overlap.unit, count: overlap.by })}
        </p>
        {pastEnd(answer.pastEndIfKept, 'when178.overlap.pastEndKeep', 'overlap-keep-past-end')}
      </div>
      <div className="overlap-choice">
        <Button variant="ghost" type="button" disabled={busy} onClick={onCancel} data-testid="overlap-cancel">
          {t('when178.overlap.cancel')}
        </Button>
      </div>
    </section>
  );
}
